import type {
  BoqRow,
  ColumnMapping,
  ExclusionReason,
  NumericParseIssue,
  RawCells,
  RowType,
  SourceType,
} from '../../types'
import { normalizeItemCode, normalizeText, normalizeUnit } from '../normalization'
import { cellToText } from './cell'
import { parseBoqNumber } from './number'

export interface RowClassificationInput {
  itemCode?: unknown
  itemName?: unknown
  itemDescription?: unknown
  unit?: unknown
  quantity?: unknown
  unitPrice?: unknown
  totalPrice?: unknown
  rawCells?: RawCells
}

export interface RowClassification {
  rowType: RowType
  exclusionReason: ExclusionReason | null
  reason: string
}

export interface ParseMappedBoqRowInput {
  cells: readonly unknown[]
  /** Optional Excel display text, used first for codes to retain leading zeroes. */
  displayCells?: readonly (string | null | undefined)[]
  mapping: ColumnMapping
  sourceType: SourceType
  fileName: string
  sheetName: string
  /** One-based worksheet row number. */
  sourceRowNumber: number
  id?: string
}

const SUMMARY_PATTERN = /(?:合计|小计|总计|累计|汇总)$/u
const SECTION_PATTERN =
  /(?:^第[一二三四五六七八九十百千万\d]+[章节篇部]|^[一二三四五六七八九十百千万\d]+[、.]|^(?:单位|单项|分部|分项|措施)工程|(?:工程|清单)$)/u

export const EXCLUSION_REASON_LABELS: Readonly<Record<ExclusionReason, string>> = {
  'blank-row': '完全空白行',
  'summary-row': '合计、小计或汇总行',
  'section-heading': '章节标题行',
  'description-only': '仅含名称或说明，缺少编码、单位、工程量和价格',
  'missing-identity': '缺少项目编码、名称和项目特征',
  'non-item-row': '非清单项目行',
}

function valuesFromRawCells(rawCells: RawCells | undefined): readonly unknown[] {
  if (rawCells === undefined) return []
  return Array.isArray(rawCells) ? rawCells : Object.values(rawCells)
}

function hasContent(value: unknown): boolean {
  if (cellToText(value).trim().length > 0) return true
  return parseBoqNumber(value).status !== 'blank'
}

export function classifyRow(input: RowClassificationInput): RowClassification {
  const itemCode = normalizeItemCode(input.itemCode)
  const itemName = normalizeText(input.itemName)
  const itemDescription = normalizeText(input.itemDescription)
  const unit = normalizeUnit(input.unit)
  const quantity = parseBoqNumber(input.quantity)
  const unitPrice = parseBoqNumber(input.unitPrice)
  const totalPrice = parseBoqNumber(input.totalPrice)
  const numericPresent = [quantity, unitPrice, totalPrice].some((value) => value.status !== 'blank')
  const canonicalContent = Boolean(
    itemCode || itemName || itemDescription || unit || numericPresent,
  )
  const rawContent = valuesFromRawCells(input.rawCells).some(hasContent)

  if (!canonicalContent && !rawContent) {
    return {
      rowType: 'blank',
      exclusionReason: 'blank-row',
      reason: EXCLUSION_REASON_LABELS['blank-row'],
    }
  }

  const isSummary = [itemCode, itemName]
    .map((value) => value.replace(/\s+/gu, ''))
    .some((value) => SUMMARY_PATTERN.test(value))
  if (isSummary) {
    return {
      rowType: 'summary',
      exclusionReason: 'summary-row',
      reason: EXCLUSION_REASON_LABELS['summary-row'],
    }
  }

  const hasCommercialData = Boolean(itemCode || unit || numericPresent)
  const descriptiveText = itemName || itemDescription
  if (descriptiveText && !hasCommercialData) {
    if (SECTION_PATTERN.test(descriptiveText.replace(/\s+/gu, ''))) {
      return {
        rowType: 'section',
        exclusionReason: 'section-heading',
        reason: EXCLUSION_REASON_LABELS['section-heading'],
      }
    }
    return {
      rowType: 'excluded',
      exclusionReason: 'description-only',
      reason: EXCLUSION_REASON_LABELS['description-only'],
    }
  }

  if (!itemCode && !itemName && !itemDescription) {
    return {
      rowType: 'excluded',
      exclusionReason: 'missing-identity',
      reason: EXCLUSION_REASON_LABELS['missing-identity'],
    }
  }

  return { rowType: 'item', exclusionReason: null, reason: '有效清单项目行' }
}

function getCell(cells: readonly unknown[], column: number | null): unknown {
  return column === null ? null : cells[column]
}

function displayText(
  cells: readonly unknown[],
  displayCells: readonly (string | null | undefined)[] | undefined,
  column: number | null,
): string {
  if (column === null) return ''
  const displayed = displayCells?.[column]
  if (displayed !== null && displayed !== undefined && displayed !== '') return displayed
  return cellToText(cells[column])
}

export function parseMappedBoqRow(input: ParseMappedBoqRowInput): BoqRow {
  const { cells, displayCells, mapping } = input
  const itemCode = displayText(cells, displayCells, mapping.itemCodeColumn)
  const itemName = displayText(cells, displayCells, mapping.itemNameColumn)
  const itemDescription = displayText(cells, displayCells, mapping.itemDescriptionColumn)
  const unit = displayText(cells, displayCells, mapping.unitColumn)
  const quantityCell = getCell(cells, mapping.quantityColumn)
  const unitPriceCell = getCell(cells, mapping.unitPriceColumn)
  const totalPriceCell = getCell(cells, mapping.totalPriceColumn)
  const parsedQuantity = parseBoqNumber(quantityCell)
  const parsedUnitPrice = parseBoqNumber(unitPriceCell)
  const parsedTotalPrice = parseBoqNumber(totalPriceCell)
  const effectiveTotalPrice =
    parsedTotalPrice.value ??
    (parsedQuantity.value !== null && parsedUnitPrice.value !== null
      ? parsedQuantity.value * parsedUnitPrice.value
      : null)
  const totalPriceSource =
    parsedTotalPrice.value !== null
      ? ('original' as const)
      : effectiveTotalPrice !== null
        ? ('calculated' as const)
        : ('missing' as const)
  const classification = classifyRow({
    itemCode,
    itemName,
    itemDescription,
    unit,
    quantity: quantityCell,
    unitPrice: unitPriceCell,
    totalPrice: totalPriceCell,
    rawCells: cells,
  })

  const numericParseIssues: NumericParseIssue[] = []
  const numericFields = [
    ['quantity', parsedQuantity],
    ['unitPrice', parsedUnitPrice],
    ['totalPrice', parsedTotalPrice],
  ] as const
  for (const [field, parsed] of numericFields) {
    if (parsed.status === 'invalid' || parsed.status === 'formula-unresolved') {
      numericParseIssues.push({
        field,
        status: parsed.status,
        sourceRowNumber: input.sourceRowNumber,
      })
    }
  }

  return {
    id:
      input.id ??
      `${input.sourceType}:${input.fileName}:${input.sheetName}:${String(input.sourceRowNumber)}`,
    sourceType: input.sourceType,
    fileName: input.fileName,
    sheetName: input.sheetName,
    sourceRowNumber: input.sourceRowNumber,
    itemCode,
    itemName,
    itemDescription,
    unit,
    quantity: parsedQuantity.value,
    unitPrice: parsedUnitPrice.value,
    totalPrice: parsedTotalPrice.value,
    effectiveTotalPrice,
    totalPriceSource,
    rawCells: [...cells],
    normalizedFields: {
      itemCode: normalizeItemCode(itemCode),
      itemName: normalizeText(itemName),
      itemDescription: normalizeText(itemDescription),
      unit: normalizeUnit(unit),
    },
    rowType: classification.rowType,
    exclusionReason: classification.exclusionReason,
    numericParseIssues,
  }
}

export function isComparableRow(row: Pick<BoqRow, 'rowType' | 'exclusionReason'>): boolean {
  return row.rowType === 'item' && row.exclusionReason === null
}
